import type { MemberRole, OrganizationDetail, OrganizationSummary } from '@viewer/protocol';
import { useEffect, useState } from 'react';
import { useApi } from '../../lib/api/useApi';
import { CreateOrgForm } from './CreateOrgForm';
import { DevicesTab } from './DevicesTab';
import { GroupsTab } from './GroupsTab';
import { MembersTab } from './MembersTab';
import { OverviewTab } from './OverviewTab';
import { SessionsTab } from './SessionsTab';
import { SettingsTab } from './SettingsTab';
import { roleLabel } from './labels';
import styles from './org.module.css';

export type OrgTab = 'overview' | 'devices' | 'groups' | 'members' | 'sessions' | 'orgSettings';

export function orgTabsForRole(role: MemberRole): OrgTab[] {
  const tabs: OrgTab[] = ['overview', 'devices', 'groups', 'members'];
  if (role !== 'member') tabs.push('sessions');
  if (role === 'owner') tabs.push('orgSettings');
  return tabs;
}

interface Props {
  organizations: OrganizationSummary[];
  /** Empresa que deve abrir (ex.: recém-criada ou convite aceito). */
  focusId: string | null;
  userId: string;
  canConnect: boolean;
  tab: OrgTab;
  onTabChange(tab: OrgTab): void;
  onConnect(code: string): void;
  /** Lista de empresas mudou (criada, apagada, renomeada). */
  onOrganizationsChanged(): Promise<void>;
  /** Papel na empresa selecionada (para as abas do Settings). */
  onRoleChange(role: MemberRole | null): void;
}

/** Conteúdo da empresa sem navegação própria — as abas ficam em Configurações. */
export function OrgScreen({
  organizations,
  focusId,
  userId,
  canConnect,
  tab,
  onTabChange,
  onConnect,
  onOrganizationsChanged,
  onRoleChange,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(focusId ?? organizations[0]?.id ?? null);
  const [creating, setCreating] = useState(organizations.length === 0);

  useEffect(() => {
    if (focusId) {
      setSelectedId(focusId);
      setCreating(false);
    }
  }, [focusId]);

  // Empresa selecionada sumiu (apagada ou saiu dela): volta para a primeira.
  useEffect(() => {
    if (selectedId && !organizations.some((o) => o.id === selectedId)) setSelectedId(organizations[0]?.id ?? null);
    if (organizations.length === 0) setCreating(true);
  }, [organizations, selectedId]);

  const detail = useApi<OrganizationDetail>(selectedId && !creating ? `/api/orgs/${selectedId}` : null);
  const role = detail.data?.role ?? organizations.find((o) => o.id === selectedId)?.role ?? null;

  useEffect(() => {
    onRoleChange(creating || !selectedId ? null : role);
  }, [creating, selectedId, role, onRoleChange]);

  if (creating || !selectedId) {
    return (
      <CreateOrgForm
        canCancel={organizations.length > 0}
        onCancel={() => setCreating(false)}
        onCreated={async (org) => {
          await onOrganizationsChanged();
          setSelectedId(org.id);
          setCreating(false);
          onTabChange('overview');
        }}
      />
    );
  }

  const org = detail.data;

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.orgName}>{org?.name ?? organizations.find((o) => o.id === selectedId)?.name}</h1>
          {role && <p className={styles.orgRole}>Você é {roleLabel[role].toLowerCase()} desta empresa</p>}
        </div>
        <div className={styles.inline}>
          {organizations.length > 1 && (
            <select
              className={styles.select}
              value={selectedId}
              onChange={(e) => {
                setSelectedId(e.target.value);
                onTabChange('overview');
              }}
              aria-label="Trocar de empresa"
            >
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setCreating(true)}>
            Nova empresa
          </button>
        </div>
      </div>

      {detail.error && <p className={styles.error}>{detail.error}</p>}
      {org && tab === 'overview' && (
        <OverviewTab
          org={org}
          onOpen={(next) => onTabChange(next)}
        />
      )}
      {org && tab === 'devices' && (
        <DevicesTab org={org} canConnect={canConnect} onConnect={onConnect} onChanged={detail.reload} />
      )}
      {org && tab === 'groups' && <GroupsTab org={org} onChanged={detail.reload} />}
      {org && tab === 'members' && <MembersTab org={org} userId={userId} onChanged={detail.reload} />}
      {org && tab === 'sessions' && <SessionsTab org={org} />}
      {org && tab === 'orgSettings' && (
        <SettingsTab
          org={org}
          onChanged={async () => {
            await onOrganizationsChanged();
            await detail.reload();
          }}
          onDeleted={async () => {
            await onOrganizationsChanged();
            setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}
